import { Mutex } from "async-mutex";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import type { LocalVideoTrack as TwilioLocalVideoTrack } from "twilio-video";
import { logger } from "../../datadog/logger";
import { CallbackError, CallbackSuccess } from "../../types";
import { getDeviceType } from "../../utils";
import {
  MediaSource,
  QualityMode,
  compareQualityMode,
  consolidateQualityMode,
  getStoredAudioVideoSettings,
  localVideoContextAtom,
  useStartVideo,
  useStopVideo,
  useStoredAudioVideoSettings,
} from "../../video-sdk";
import {
  addProcessor,
  audioVideoSettingsByQualityMode,
  getCurrentProcessor,
  getTwilioTrackFromDelegator,
  shouldSuggestPerformanceModeAtom,
  videoQualityAtom,
} from "../../video-sdk/twilio";
import { useIsZoomEnabled as useBaseIsZoomEnabled } from "../../hooks";

export enum ChangeVideoQualityStatusCode {
  MISSING_TRACK = "missing_track",
  FAILED_TO_REQUIRE_TRACK = "failed_to_require_track",
  ERROR = "error",
  CHANGED = "changed",
  NOOP = "noop",
}

type ChangeVideoQualityCallbackResponse =
  | CallbackSuccess<TwilioLocalVideoTrack | null, ChangeVideoQualityStatusCode>
  | CallbackError<ChangeVideoQualityStatusCode>;

type GetMaxVideoQualityOpts = {
  suggestedPerformanceModeConstraint?: ConstraintMode;
  targetQuality?: QualityMode;
};

type VideoQualityCallbackHookOpts = {
  useIsZoomEnabled?: typeof useBaseIsZoomEnabled;
};

export enum QualityPersistanceType {
  STORAGE_AND_MEMORY = "storage_and_memory",
  MEMORY = "memory",
  NONE = "none",
}

export enum ConstraintMode {
  AUTO = "auto",
  ALWAYS = "always",
  NEVER = "never",
}

export const VIDEO_QUALITY_NOOP_STATUS_RESPONSE: Readonly<ChangeVideoQualityCallbackResponse> =
  {
    status: "success",
    code: ChangeVideoQualityStatusCode.NOOP,
  };

const trackQualityMutex = new Mutex();

/**
 * Returns a function that changes the video quality the local video track, and
 * saves the new quality mode.
 */
export function useChangeVideoQualityCallback({
  useIsZoomEnabled = useBaseIsZoomEnabled,
}: VideoQualityCallbackHookOpts = {}) {
  const startVideo = useStartVideo();
  const stopVideo = useStopVideo();
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const getLocalVideoDelegator = useAtomCallback(
    useCallback((get) => get(localVideoContextAtom).delegator, []),
  );
  const getLocalVideoStatus = useAtomCallback(
    useCallback((get) => get(localVideoContextAtom).status, []),
  );
  const setVideoQuality = useSetAtom(videoQualityAtom);
  const getCurrentVideoQuality = useAtomCallback(
    useCallback((get) => get(videoQualityAtom), []),
  );
  const [, checkZoomIsEnabled] = useIsZoomEnabled();

  return useCallback(
    async (
      qualityMode: QualityMode,
      opts?: {
        persistanceType?: QualityPersistanceType;
      },
    ): Promise<ChangeVideoQualityCallbackResponse> => {
      if (checkZoomIsEnabled()) {
        return VIDEO_QUALITY_NOOP_STATUS_RESPONSE;
      }

      const { persistanceType = QualityPersistanceType.STORAGE_AND_MEMORY } =
        opts ?? {};

      const checkShouldChangeQuality = ():
        | { shouldChange: true; response: TwilioLocalVideoTrack }
        | {
            shouldChange: false;
            response: ChangeVideoQualityCallbackResponse;
          } => {
        if (qualityMode === getCurrentVideoQuality()) {
          return {
            shouldChange: false,
            response: {
              status: "success",
              code: ChangeVideoQualityStatusCode.NOOP,
            },
          };
        }
        // TODO CARED-2738: this hook has not been adapter-ified yet and needs to be dealt with so we don't use a twilio track here
        const track = getTwilioTrackFromDelegator(getLocalVideoDelegator());
        if (!track)
          return {
            shouldChange: false,
            response: {
              status: "error",
              code: ChangeVideoQualityStatusCode.MISSING_TRACK,
            },
          };

        return {
          shouldChange: true,
          response: track as TwilioLocalVideoTrack,
        };
      };

      const { shouldChange, response } = checkShouldChangeQuality();
      if (!shouldChange) return response;

      const releaseMutex = await trackQualityMutex.acquire();
      try {
        const { shouldChange, response } = checkShouldChangeQuality();
        if (!shouldChange) return response;
        const track: TwilioLocalVideoTrack | undefined = response;

        if (qualityMode === getCurrentVideoQuality()) {
          return {
            status: "success",
            code: ChangeVideoQualityStatusCode.NOOP,
          };
        }

        const deviceId = track.mediaStreamTrack.getSettings().deviceId;
        const trackOptions = {
          ...audioVideoSettingsByQualityMode[qualityMode][getDeviceType()]
            .video[MediaSource.Camera],
          deviceId: {
            exact: deviceId,
          },
        };
        const videoIsStopped = await stopVideo(true);
        const videoIsRestarted = await startVideo({ trackOptions });
        const finalTrackState = getLocalVideoStatus();

        if (!videoIsStopped || !videoIsRestarted) {
          logger.error("Unable to change video quality mode", {
            qualityMode,
            trackOptions,
            videoIsStopped,
            videoIsRestarted,
            delegatorState: finalTrackState,
          });
          return {
            status: "error",
            code: ChangeVideoQualityStatusCode.ERROR,
          };
        }

        // restore previous processing
        const processor = getCurrentProcessor();
        if (processor) {
          // restarting the video should update by this point of execution
          const latestTrack = getTwilioTrackFromDelegator(
            getLocalVideoDelegator(),
          ) as TwilioLocalVideoTrack;
          addProcessor(latestTrack!, processor);
        }

        // store the new quality mode
        if (
          persistanceType === QualityPersistanceType.STORAGE_AND_MEMORY ||
          persistanceType === QualityPersistanceType.MEMORY
        ) {
          setVideoQuality(qualityMode);
        }
        const storedQualityMode = consolidateQualityMode(qualityMode);
        if (persistanceType === QualityPersistanceType.STORAGE_AND_MEMORY) {
          setStoredAudioVideoSettings((settings) => ({
            ...settings,
            videoQualityMode: storedQualityMode,
          }));
        }

        return {
          status: "success",
          code: ChangeVideoQualityStatusCode.CHANGED,
        };
      } catch (e) {
        const error = e as Error;
        logger.error(
          "Unable to change video quality mode",
          {
            qualityMode,
          },
          error as Error,
        );
        return {
          status: "error",
          code: ChangeVideoQualityStatusCode.ERROR,
        };
      } finally {
        releaseMutex();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

export function useDegradeVideoQualityCallback({
  useIsZoomEnabled = useBaseIsZoomEnabled,
}: VideoQualityCallbackHookOpts = {}) {
  const changeVideoQuality = useChangeVideoQualityCallback();
  const getMaxVideoQuality = useGetVideoQualityLimitCallback();
  const getCurrentVideoQuality = useAtomCallback(
    useCallback((get) => get(videoQualityAtom), []),
  );
  const [, checkZoomIsEnabled] = useIsZoomEnabled();

  return useCallback(
    async (
      opts?: Omit<GetMaxVideoQualityOpts, "shouldReturnLowerQuality">,
    ): Promise<ChangeVideoQualityCallbackResponse> => {
      if (checkZoomIsEnabled()) {
        return VIDEO_QUALITY_NOOP_STATUS_RESPONSE;
      }
      return await changeVideoQuality(
        getMaxVideoQuality({
          targetQuality: getCurrentVideoQuality(),
          ...opts,
        }),
        {
          persistanceType: QualityPersistanceType.MEMORY,
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

export function useRevertVideoQualityCallback({
  useIsZoomEnabled = useBaseIsZoomEnabled,
}: VideoQualityCallbackHookOpts = {}) {
  const changeVideoQuality = useChangeVideoQualityCallback();
  const getMaxVideoQuality = useGetVideoQualityLimitCallback();
  const [, checkZoomIsEnabled] = useIsZoomEnabled();

  return useCallback(
    async (): Promise<ChangeVideoQualityCallbackResponse> => {
      if (checkZoomIsEnabled()) {
        return VIDEO_QUALITY_NOOP_STATUS_RESPONSE;
      }
      return await changeVideoQuality(
        getMaxVideoQuality({
          targetQuality: getStoredAudioVideoSettings().videoQualityMode,
        }),
        {
          persistanceType: QualityPersistanceType.MEMORY,
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

/**
 * Returns a function that returns the maximum video quality that can be used,
 * based on the current state of the app. If `targetQuality` is provided, then
 * the function will return the lower of the two.
 *
 * Constraints:
 *  * Low quality:
 *    * If performance mode is suggested
 *  * High quality:
 *    * If none of the above constraints are met
 */
export function useGetVideoQualityLimitCallback() {
  const getShouldSuggestPerformanceMode = useAtomCallback(
    useCallback((get) => get(shouldSuggestPerformanceModeAtom), []),
  );

  return useCallback(
    (opts?: GetMaxVideoQualityOpts): QualityMode => {
      const {
        suggestedPerformanceModeConstraint = ConstraintMode.AUTO,
        targetQuality,
      } = opts ?? {};

      const shouldSuggestPerformanceMode =
        suggestedPerformanceModeConstraint !== ConstraintMode.NEVER &&
        (suggestedPerformanceModeConstraint === ConstraintMode.ALWAYS ||
          getShouldSuggestPerformanceMode());

      let maxQuality = QualityMode.High;
      if (shouldSuggestPerformanceMode) {
        maxQuality = QualityMode.Low;
      }

      if (!targetQuality) {
        return maxQuality;
      }

      if (compareQualityMode(targetQuality, maxQuality) < 0) {
        return targetQuality;
      }

      return maxQuality;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

export function useVideoQualityCallbacks() {
  return {
    getVideoQualityLimit: useGetVideoQualityLimitCallback(),
    changeVideoQuality: useChangeVideoQualityCallback(),
    degradeVideoQuality: useDegradeVideoQualityCallback(),
    revertVideoQuality: useRevertVideoQualityCallback(),
  };
}
